import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, GraduationCap, Info } from "lucide-react";
import { ClientProduct } from "@/api/clientService";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatCurrency } from "@/lib/formatters";
import { codeSeries } from "@/lib/utils";

interface ClientViewProps {
  client: {
    id: number;
    name: string;
    clientAdjustment?: number;
  };
  products: ClientProduct[];
  loading: boolean;
  cycleYear?: number | null;
}

export function ClientView({ client, products, loading, cycleYear }: ClientViewProps) {
  if (loading) {
    return (
      <p className="text-center text-gray-500 mt-6">
        Carregando produtos do cliente...
      </p>
    );
  }

  if (!products.length) {
    return (
      <p className="text-center text-gray-500 mt-6">
        Nenhum produto vinculado a este cliente.
      </p>
    );
  }

  // 🔹 Separar produtos por tipo de uso (Aluno / Professor)
  const alunos = products.filter((p) => p.isActive &&
    p.materialUseName?.toLowerCase().includes("aluno")
  );
  const professores = products.filter((p) => p.isActive &&
    p.materialUseName?.toLowerCase().includes("professor")
  );

  alunos.sort((a, b) => {
    const codeA = codeSeries(a.seriesName);
    const codeB = codeSeries(b.seriesName);
    if (codeA === codeB) return 0;
    if (codeA === null) return 1;
    if (codeB === null) return -1;
    return codeA - codeB;
  });
  professores.sort((a, b) => {
    const codeA = codeSeries(a.seriesName);
    const codeB = codeSeries(b.seriesName);
    if (codeA === codeB) return 0;
    if (codeA === null) return 1;
    if (codeB === null) return -1;
    return codeA - codeB;
  });

  const totalAcvAlunos = alunos.reduce((sum, p) => sum + ((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0)), 0);
  const totalAcvProfessores = professores.reduce((sum, p) => sum + ((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0)), 0);

  // 🔹 Função para renderizar o Accordion de séries
  const renderSeriesAccordion = (items: ClientProduct[]) => {
    const seriesGroups = Object.values(
      items.filter(item => item.isActive).reduce((acc, item) => {
        const { seriesName } = item;
        const itemTotal = (item.priceWithoutDiscount ?? item.productPrice ?? 0) * (item.contractualQuantity ?? 0);
        const key = seriesName ?? "Sem Série";

        if (!acc[key]) {
          acc[key] = {
            seriesName: key,
            totalWithDiscount: 0,
            dados: [],
          };
        }

        acc[key].dados.push(item);
        acc[key].totalWithDiscount += itemTotal;

        return acc;
      }, {} as Record<string, { seriesName: string; totalWithDiscount: number; dados: ClientProduct[] }>)
    );

    seriesGroups.sort((a, b) => {
      const codeA = codeSeries(a.seriesName);
      const codeB = codeSeries(b.seriesName);
      if (codeA === codeB) return 0;
      if (codeA === null) return 1;
      if (codeB === null) return -1;
      return codeA - codeB;
    });

    if (seriesGroups.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 text-gray-500 mt-4 p-4 bg-gray-50 rounded-md">
          <Info className="h-4 w-4" />
          <span>Nenhuma série encontrada para este tipo de produto.</span>
        </div>
      );
    }

    return (
      <Accordion type="single" collapsible className="w-full space-y-3">
        {seriesGroups.map((group) => (
          <AccordionItem
            value={group.seriesName}
            key={group.seriesName}
            className="border w-full border-gray-200 rounded-xl transition-all duration-300 hover:shadow-sm data-[state=open]:bg-gray-100/50"
          >
            <AccordionTrigger asChild className="w-full cursor-pointer p-6 text-lg font-semibold text-gray-800 hover:no-underline">
              <div className="flex justify-between items-center w-full mr-3">
                <div className="flex items-center gap-4">
                  <span>{group.seriesName}</span>
                  <Badge variant="secondary" className="text-sm">
                    {group.dados.length} produtos
                  </Badge>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-medium text-muted-foreground">
                    ACV da Série
                  </span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(group.totalWithDiscount)}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-6 pt-0">
              <div className="grid gap-3">
                {group.dados.map((p) => (
                  <Card
                    key={p.profileClientId}
                    className="border border-gray-200 rounded-xl bg-gray-50/50"
                  >
                    <CardContent className="flex justify-between items-center py-4 px-6">
                      <div className="flex flex-col gap-1">
                        <h5 className="font-semibold text-base">
                          {p.productDescription}
                          {(p as any).productSku && (
                            <Badge
                              variant="outline"
                              className="mx-2 text-xs text-gray-700 border-gray-200"
                            >
                              SKU: {(p as any).productSku}
                            </Badge>
                          )}
                        </h5>
                        <div className="text-sm text-muted-foreground">
                          Série: {p.seriesName ?? "—"} • Segmento:{" "}
                          {p.segmentName ?? "—"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Ano letivo: {p.productSchoolYear ?? "—"}
                        </div>
                      </div>

                      <div className="text-right space-y-1">
                        <div className="text-sm text-muted-foreground">
                          Qtd. Referência:
                          <span className="ml-1 font-medium text-foreground">
                            {p.contractualQuantity ?? 0}
                          </span>
                        </div>
                        <div className="text-sm text-blue-600 font-semibold">
                          Valor unitário:{" "}
                          {formatCurrency(p.priceWithoutDiscount ?? p.productPrice ?? 0)}
                        </div>
                        <div className="text-base font-bold text-green-600">
                          ACV:{" "}
                          {formatCurrency((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xl font-semibold text-blue-600">
          Produtos Contratados
        </h3>
        <Badge variant="secondary" className="text-xs">
          Ciclo: {cycleYear ?? "atual (automático)"}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Visualize todos os produtos contratados e suas respectivas quantidades,
        descontos e valores totais.
      </p>

      <Accordion type="single" collapsible className="w-full space-y-3">
        {alunos.length > 0 && (
          <AccordionItem
            value="aluno-group"
            className="border w-full border-gray-200 rounded-xl transition-all duration-300 hover:shadow-sm data-[state=open]:bg-blue-50/50"
          >
            <AccordionTrigger asChild className="w-full cursor-pointer p-6 text-lg font-semibold text-gray-800 hover:no-underline">
              <div className="flex justify-between w-full items-center mr-3">
                <div className="flex items-center gap-4">
                  <BookOpen className="h-5 w-5 text-blue-500" />
                  <span>Aluno</span>
                  <Badge variant="secondary" className="text-sm">
                    {alunos.length} produtos
                  </Badge>
                </div>
                <div className="flex flex-col items-end gap-1 text-right">
                  <span className="text-sm font-medium text-muted-foreground">
                    ACV Total
                  </span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(totalAcvAlunos)}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-6 pt-0 grid gap-3 max-h-[800px] overflow-y-auto">
              {renderSeriesAccordion(alunos)}
            </AccordionContent>
          </AccordionItem>
        )}

        {professores.length > 0 && (
          <AccordionItem
            value="professor-group"
            className="border w-full border-gray-200 rounded-xl transition-all duration-300 hover:shadow-sm data-[state=open]:bg-blue-50/50"
          >
            <AccordionTrigger asChild className="w-full p-6 text-lg cursor-pointer font-semibold text-gray-800 hover:no-underline">
              <div className="flex justify-between items-center  w-full mr-3">
                <div className="flex items-center gap-4">
                  <GraduationCap className="h-5 w-5 text-blue-500" />
                  <span>Professor</span>
                  <Badge variant="secondary" className="text-sm">
                    {professores.length} produtos
                  </Badge>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-medium text-muted-foreground">
                    ACV Total
                  </span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(totalAcvProfessores)}
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-6 pt-0 grid gap-3 max-h-[800px] overflow-y-auto">
              {renderSeriesAccordion(professores)}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}